import { useState, useMemo, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Slide,
  IconButton,
  Tab,
  Tabs,
  Divider,
  Grid,
  Card,
  Chip,
  Stack,
  useTheme,
  CircularProgress,
  Alert,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import CloseIcon from "@mui/icons-material/Close";
import { TabContext, TabPanel } from "@mui/lab";
import { CoamAreaFeature } from "../api/coamArea";
import { ProjectCard } from "../../projects/components/ProjectCard";
import {
  useQuickFilter,
  useGeospatialProjects,
  useWorkstackByProgramme,
  useWorkstackByWorkstream,
} from "../mutations";
import { projectApi } from "../../projects/api/project";
import { useQueryClient } from "@tanstack/react-query";
import { PaginationControls } from "../../../shared/components/PaginationControls";
import { qk } from "../../../lib/queryKeys";

interface CoamAreaInfoProps {
  selectedFeature: CoamAreaFeature | null;
  setSelectedFeature: (feature: CoamAreaFeature | null) => void;
}

const KPICard = ({
  label,
  value,
  subValue,
}: {
  label: string | number;
  value: string | number;
  subValue?: string | number;
}) => {
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body1" color="text.secondary" noWrap gutterBottom>
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
        {subValue !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {subValue}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export const CoamAreaInfo: React.FC<CoamAreaInfoProps> = ({
  selectedFeature,
  setSelectedFeature,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState("delivery-statistics");
  const [breakdownTab, setBreakdownTab] = useState("programme");
  const [workstackBreakdownTab, setWorkstackBreakdownTab] = useState("programme");
  const [selectedPhase, setSelectedPhase] = useState<number | "">("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [yearFilter, setYearFilter] = useState<
    "total" | "thisYear" | "nextYear" | "completed"
  >("total");

  const regionId = selectedFeature?.properties.id ?? null;

  const { data: quickFilterData, isLoading: isLoadingQuickFilter } =
    useQuickFilter(regionId);

  const { data: workstackData, isLoading: isLoadingWorkstack } = useWorkstackByProgramme(regionId);
  const { data: programmeData, isLoading: isLoadingProgrammeData } = useWorkstackByWorkstream(regionId);

  const defaultPhase = useMemo(() => {
    if (!quickFilterData?.data || quickFilterData.data.length === 0)
      return null;
    return quickFilterData.data[0];
  }, [quickFilterData]);

  const currentPhaseId = useMemo(() => {
    if (typeof selectedPhase === "number") return selectedPhase;
    return defaultPhase?.id || undefined;
  }, [selectedPhase, defaultPhase]);

  const currentPhase = useMemo(() => {
    if (!quickFilterData?.data) return null;
    if (typeof selectedPhase === "number") {
      return quickFilterData.data.find((p) => p.id === selectedPhase) || null;
    }
    return defaultPhase;
  }, [quickFilterData, selectedPhase, defaultPhase]);

  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useGeospatialProjects({
    phase_type_id: currentPhaseId,
    region_id: regionId,
    phase_name: currentPhase?.name,
    assign_to_me: true,
    page,
    limit,
  });

  const phases = useMemo(() => {
    if (!quickFilterData?.data) return [];
    return quickFilterData.data.sort(
      (a, b) => a.sequence_order - b.sequence_order
    );
  }, [quickFilterData]);

  const deliveryStatistics = useMemo(() => {
    if (!quickFilterData?.data)
      return { phaseBreakdown: [], sectionBreakdown: [], workstreamBreakdown: [] };


    const phaseBreakdown = quickFilterData.data
      .filter((item) => item.id !== 11)
      .map((item) => ({
        phase: item.name,
        count: item.project_count,
      }));

    const sectionBreakdown =
      workstackData?.data?.breakdown?.total?.map((item) => ({
        section: item.programme,
        count: parseInt(item.count, 10),
      })) || [];

    // Add workstream breakdown - you can modify this based on your actual data structure
    const workstreamBreakdown =
      programmeData?.data?.breakdown?.total?.map((item) => ({
        workstream: item.programme,
        count: parseInt(item.count, 10),
      })) || [];

    return { phaseBreakdown, sectionBreakdown, workstreamBreakdown };
  }, [quickFilterData, workstackData]);

  const workstackBreakdown = useMemo(() => {
    if (!workstackData?.data) return { programme: [], workstream: [] };

    const programmeBreakdown = workstackData.data.breakdown[yearFilter] || [];

    // Add workstream breakdown for workstack - modify based on your API structure
    const workstreamBreakdown = workstackData?.data?.workstreamBreakdown?.[yearFilter]?.map((item: any) => ({
      workstream: item.workstream || item.name,
      count: parseInt(item.count, 10),
    })) || [];

    return { programme: programmeBreakdown, workstream: workstreamBreakdown };
  }, [workstackData, yearFilter]);

  const projects = useMemo(() => {
    if (!projectsData?.data?.rows) return [];
    return projectsData.data.rows.map((project) => ({
      id: String(project.id),
      title: project.title,
      location: `${project.address || ""}, ${project.county || ""}`.trim(),
      workType: project.programme,
      workTypeDescription: "", 
      approvalStage: project.phase_name,
      progress: project.progress.percentage,
      projectId: project.project_reference,
      phases: project.phases,
      ragStatus: project.rag,
      isPinned: project.is_fav,
    }));
  }, [projectsData]);

  const featureKey = useMemo(() => {
    if (!selectedFeature) return "no-feature";
    return (
      selectedFeature.properties.id?.toString() ||
      selectedFeature.properties.name ||
      JSON.stringify(selectedFeature)
    );
  }, [selectedFeature]);

  useEffect(() => {
    if (!selectedFeature) {
      setSelectedPhase("");
      setPage(1);
    }
  }, [selectedFeature]);

  useEffect(() => {
    setPage(1);
  }, [selectedPhase]);

  const handleClose = () => {
    setSelectedFeature(null);
    setSelectedPhase("");
    setPage(1);
  };

  const handleTabChange = (_e: React.SyntheticEvent, tab: string) => {
    setCurrentTab(tab);
  };

  const handleBreakdownTabChange = (_e: React.SyntheticEvent, tab: string) => {
    setBreakdownTab(tab);
  };

  const handleWorkstackBreakdownTabChange = (_e: React.SyntheticEvent, tab: string) => {
    setWorkstackBreakdownTab(tab);
  };

  const handlePhaseClick = (phaseId: number) => {
    if (selectedPhase === phaseId) {
      setSelectedPhase("");
    } else {
      setSelectedPhase(phaseId);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleProjectClick = (projectId: string) => { };

  const handlePinToggle = async (projectId: string, isPinned: boolean) => {
    try {
      await projectApi.pinProjects({
        id: projectId,
        status: !isPinned,
      });
      await queryClient.invalidateQueries({
        queryKey: qk.geospatial.projects({
          region_id: regionId,
          phase_type_id: currentPhaseId,
          phase_name: currentPhase?.name,
          page,
          limit,
        }),
      });
    } catch (error) {
      console.error("Error toggling pin:", error);
    }
  };

  return (
    <Slide
      direction="left"
      in={selectedFeature !== null}
      mountOnEnter
      unmountOnExit
      timeout={500}
      key={featureKey}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "flex-end",
          px: 4,
          py: 4,
          pointerEvents: "none",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: { xs: "100%", sm: "50%", md: "50%", lg: "45%", xl: "35%" },
            height: "100%",
            borderRadius: 2,
            pointerEvents: "auto",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              backgroundColor: "action.hover",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                COAM Area
              </Typography>
              <Typography variant="h6">
                {selectedFeature?.properties.name}
              </Typography>
            </Box>

            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TabContext value={currentTab}>
            <Box>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                sx={{ minHeight: "auto" }}
              >
                <Tab
                  sx={{ height: "auto", minHeight: "auto" }}
                  label="Delivery Statistics"
                  value="delivery-statistics"
                />
                <Tab
                  sx={{ height: "auto", minHeight: "auto" }}
                  label="Workstack"
                  value="workstack"
                />
                <Tab
                  sx={{ height: "auto", minHeight: "auto" }}
                  label="Project Performance"
                  value="project-performance"
                />
              </Tabs>
              <Divider />
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
              }}
            >
              <TabPanel value="delivery-statistics" sx={{ p: 0 }}>
                {isLoadingQuickFilter || isLoadingWorkstack ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 200,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <Box>
                      <Typography variant="overline">
                        Breakdown by Project Phase
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {deliveryStatistics.phaseBreakdown.map((item) => (
                          <Grid size={{ xs: 12, sm: 6 }} key={item.phase}>
                            <KPICard
                              label={item.phase}
                              value={item.count}
                              subValue="projects"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {(deliveryStatistics.sectionBreakdown.length > 0 || deliveryStatistics.workstreamBreakdown.length > 0) && (
                      <Box>
                        <TabContext value={breakdownTab}>
                          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                              value={breakdownTab}
                              onChange={handleBreakdownTabChange}
                              sx={{ minHeight: 'auto' }}
                            >
                              <Tab
                                sx={{ height: 'auto', minHeight: 'auto', textTransform: 'none' }}
                                label="Breakdown By Programme"
                                value="programme"
                              />
                              <Tab
                                sx={{ height: 'auto', minHeight: 'auto', textTransform: 'none' }}
                                label="Breakdown By Workstream"
                                value="workstream"
                              />
                            </Tabs>
                          </Box>

                          <TabPanel value="programme" sx={{ p: 0, pt: 2 }}>
                            {deliveryStatistics.sectionBreakdown.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <PieChart
                                  series={[
                                    {
                                      data: deliveryStatistics.sectionBreakdown.map(
                                        (item, index) => ({
                                          id: index,
                                          value: item.count,
                                          label: item.section,
                                        })
                                      ),
                                      innerRadius: 30,
                                      outerRadius: 100,
                                      paddingAngle: 2,
                                      arcLabel: (item) => `${item.value}`,
                                      highlightScope: {
                                        fade: "global",
                                        highlight: "item",
                                      },
                                      faded: {
                                        innerRadius: 30,
                                        additionalRadius: -30,
                                        color: "gray",
                                      },
                                    },
                                  ]}
                                  height={300}
                                  margin={{
                                    left: 0,
                                    right: 10,
                                    top: 10,
                                    bottom: 10,
                                  }}
                                  slotProps={{
                                    legend: {
                                      position: {
                                        vertical: "middle",
                                        horizontal: "end",
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            ) : (
                              <Alert severity="info">No programme data available</Alert>
                            )}
                          </TabPanel>

                          <TabPanel value="workstream" sx={{ p: 0, pt: 2 }}>
                            {deliveryStatistics.workstreamBreakdown.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <PieChart
                                  series={[
                                    {
                                      data: deliveryStatistics.workstreamBreakdown.map(
                                        (item: { count: any; workstream: any; }, index: any) => ({
                                          id: index,
                                          value: item.count,
                                          label: item.workstream,
                                        })
                                      ),
                                      innerRadius: 30,
                                      outerRadius: 100,
                                      paddingAngle: 2,
                                      arcLabel: (item) => `${item.value}`,
                                      highlightScope: {
                                        fade: "global",
                                        highlight: "item",
                                      },
                                      faded: {
                                        innerRadius: 30,
                                        additionalRadius: -30,
                                        color: "gray",
                                      },
                                    },
                                  ]}
                                  height={300}
                                  margin={{
                                    left: 0,
                                    right: 10,
                                    top: 10,
                                    bottom: 10,
                                  }}
                                  slotProps={{
                                    legend: {
                                      position: {
                                        vertical: "middle",
                                        horizontal: "end",
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            ) : (
                              <Alert severity="info">No workstream data available</Alert>
                            )}
                          </TabPanel>
                        </TabContext>
                      </Box>
                    )}
                  </Box>
                )}
              </TabPanel>

              <TabPanel value="workstack" sx={{ p: 0 }}>
                {isLoadingWorkstack ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 200,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : workstackData?.data ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 1, flexWrap: "wrap" }}
                      >
                        <Chip
                          label={`Total (${workstackData.data.summary.total})`}
                          onClick={() => setYearFilter("total")}
                          color={yearFilter === "total" ? "primary" : "default"}
                          variant={
                            yearFilter === "total" ? "filled" : "outlined"
                          }
                        />
                        <Chip
                          label={`This Year (${workstackData.data.summary.thisYear})`}
                          onClick={() => setYearFilter("thisYear")}
                          color={
                            yearFilter === "thisYear" ? "primary" : "default"
                          }
                          variant={
                            yearFilter === "thisYear" ? "filled" : "outlined"
                          }
                        />
                        <Chip
                          label={`Next Year (${workstackData.data.summary.nextYear})`}
                          onClick={() => setYearFilter("nextYear")}
                          color={
                            yearFilter === "nextYear" ? "primary" : "default"
                          }
                          variant={
                            yearFilter === "nextYear" ? "filled" : "outlined"
                          }
                        />
                        <Chip
                          label={`Completed (${workstackData.data.summary.completed})`}
                          onClick={() => setYearFilter("completed")}
                          color={
                            yearFilter === "completed" ? "primary" : "default"
                          }
                          variant={
                            yearFilter === "completed" ? "filled" : "outlined"
                          }
                        />
                      </Stack>
                    </Box>

                    {(workstackBreakdown.programme.length > 0 || workstackBreakdown.workstream.length > 0) && (
                      <Box>
                        <TabContext value={workstackBreakdownTab}>
                          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                              value={workstackBreakdownTab}
                              onChange={handleWorkstackBreakdownTabChange}
                              sx={{ minHeight: 'auto' }}
                            >
                              <Tab
                                sx={{ height: 'auto', minHeight: 'auto', textTransform: 'none' }}
                                label="Breakdown By Programme"
                                value="programme"
                              />
                              <Tab
                                sx={{ height: 'auto', minHeight: 'auto', textTransform: 'none' }}
                                label="Breakdown By Workstream"
                                value="workstream"
                              />
                            </Tabs>
                          </Box>

                          <TabPanel value="programme" sx={{ p: 0, pt: 2 }}>
                            {workstackBreakdown.programme.length > 0 ? (
                              <Grid container spacing={2}>
                                {workstackBreakdown.programme.map((item) => (
                                  <Grid size={{ xs: 12, sm: 6 }} key={item.programme}>
                                    <KPICard
                                      label={item.programme}
                                      value={item.count}
                                      subValue="projects"
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Alert severity="info">No programme data available</Alert>
                            )}
                          </TabPanel>

                          <TabPanel value="workstream" sx={{ p: 0, pt: 2 }}>
                            {workstackBreakdown.workstream.length > 0 ? (
                              <Grid container spacing={2}>
                                {workstackBreakdown.workstream.map((item: { workstream: any; count: string | number; }) => (
                                  <Grid size={{ xs: 12, sm: 6 }} key={item.workstream}>
                                    <KPICard
                                      label={item.workstream}
                                      value={item.count}
                                      subValue="projects"
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Alert severity="info">No workstream data available</Alert>
                            )}
                          </TabPanel>
                        </TabContext>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Alert severity="info">No workstack data available</Alert>
                )}
              </TabPanel>

              <TabPanel value="project-performance" sx={{ p: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {isLoadingQuickFilter ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 100,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {phases.map((phase) => {
                          const isSelected =
                            selectedPhase === phase.id ||
                            (!selectedPhase && defaultPhase?.id === phase.id);
                          return (
                            <Chip
                              key={phase.id}
                              disabled={isLoadingProjects}
                              variant={isSelected ? "filled" : "outlined"}
                              color={isSelected ? "primary" : "default"}
                              label={`${phase.name} (${phase.project_count})`}
                              onClick={() => handlePhaseClick(phase.id)}
                            />
                          );
                        })}
                      </Box>

                      {projectsError && (
                        <Alert severity="error">
                          Failed to load projects. Please try again.
                        </Alert>
                      )}

                      {isLoadingProjects ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 200,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <>
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              {projectsData?.data?.pagination?.total || 0}{" "}
                              project
                              {(projectsData?.data?.pagination?.total || 0) !==
                                1
                                ? "s"
                                : ""}{" "}
                              found
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              flex: 1,
                              minHeight: 0,
                            }}
                          >
                            {projects.length === 0 ? (
                              <Alert severity="info">
                                No projects found for the selected filters.
                              </Alert>
                            ) : (
                              <>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    flex: 1,
                                    overflowY: "auto",
                                  }}
                                >
                                  {projects.map((project) => (
                                    <ProjectCard
                                      key={project.id}
                                      title={project.title}
                                      location={project.location}
                                      workType={project.workType}
                                      workTypeDescription={project.workTypeDescription}
                                      approvalStage={project.approvalStage}
                                      progress={project.progress}
                                      projectId={project.projectId}
                                      phases={project.phases}
                                      ragStatus={project.ragStatus}
                                      isPinned={project.isPinned}
                                      handleClick={() =>
                                        handleProjectClick(project.id)
                                      }
                                      handlePinToggle={(pin) =>
                                        handlePinToggle(project.id, pin)
                                      }
                                    />
                                  ))}
                                </Box>

                                {projectsData?.data?.pagination &&
                                  projectsData.data.pagination.total &&
                                  projectsData.data.pagination.limit && (
                                    <Box sx={{ flexShrink: 0 }}>
                                      <PaginationControls
                                        total={
                                          projectsData.data.pagination.total
                                        }
                                        rowsPerPage={
                                          projectsData.data.pagination.limit
                                        }
                                        currentPage={
                                          projectsData.data.pagination.page
                                        }
                                        handleRowsPerPage={
                                          handleRowsPerPageChange
                                        }
                                        handlePageChange={handlePageChange}
                                        
                                      />
                                    </Box>
                                  )}
                              </>
                            )}
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Box>
              </TabPanel>
            </Box>
          </TabContext>
        </Paper>
      </Box>
    </Slide>
  );
};
